<script>
const resolveHeight = (el) =>
  el ? Math.ceil(el.getBoundingClientRect().height) : 0

export default {
  name: 'HeightTransition',
  props: {
    contentTransition: {
      type: String,
      default: '',
    },
    mode: {
      type: String,
      default: 'out-in',
    },
    duration: {
      type: Number,
      default: 300,
    },
  },
  data() {
    return {
      heightPx: null,
      rafId: null,
    }
  },
  computed: {
    containerStyle() {
      return {
        '--auto-height-duration': `${this.duration}ms`,
        ...(this.heightPx !== null && {
          height: `${this.heightPx}px`,
          overflow: 'hidden',
        }),
      }
    },
  },
  beforeDestroy() {
    this.cancelFrame()
  },
  methods: {
    cancelFrame() {
      if (this.rafId === null) return

      cancelAnimationFrame(this.rafId)

      this.rafId = null
    },
    release() {
      this.heightPx = null
      this._afterLeavePending = false

      this.cancelFrame()
    },
    onBeforeLeave(el) {
      if (!this.contentTransition) return

      this.heightPx = resolveHeight(el)
    },
    onAfterLeave() {
      if (!this.contentTransition) return

      this._afterLeavePending = true

      this.rafId = requestAnimationFrame(() => {
        if (this._afterLeavePending) this.release()
      })
    },
    onEnter(el) {
      this._afterLeavePending = false

      if (!this.contentTransition) return

      const from = this.heightPx ?? resolveHeight(this.$el)

      if (!from) {
        this.release()
        return
      }

      this.heightPx = from

      this.cancelFrame()

      this.rafId = requestAnimationFrame(() => {
        const to = resolveHeight(el)

        if (!to || from === to) {
          this.release()
          return
        }

        this.rafId = requestAnimationFrame(() => {
          this.heightPx = to
          this.rafId = null
        })
      })
    },
  },
}
</script>

<template>
  <div class="auto-height-transition" :style="containerStyle">
    <transition
      :name="contentTransition"
      :mode="mode"
      @before-leave="onBeforeLeave"
      @after-leave="onAfterLeave"
      @enter="onEnter"
      @after-enter="release"
      @enter-cancelled="release"
      @leave-cancelled="release"
    >
      <slot />
    </transition>
  </div>
</template>

<style scoped>
.auto-height-transition {
  transition: height var(--auto-height-duration, 300ms) ease;
}
</style>
